import React from "react";
import { Alert, AlertTitle, Box, Container, Stack, Typography } from "@mui/material";
import { Form, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { CheckboxElement, TextFieldElement } from "react-hook-form-mui";
import { zodResolver } from "@hookform/resolvers/zod";
import { apiClientHooks } from "../bootstrapping/InitApiClient";
import { Helmet } from "react-helmet-async";
import { LocalFormSchema, zLocalFormSchema } from "../logic/applicationUpdateSchema";
import StringFormattedControlledDatePicker from "../components/StringFormattedControlledDatePicker";
import EnumBackedAutocomplete from "../components/EnumBackedAutocomplete";
import { asOptions, countryCodeMapping, driverLicenceTypeMapping } from "../utils/option-data";
import HiddenInputForClearFields from "../components/HiddenInputForClearFields";
import { TestingInfoBlock, TestingInfoBlockFormErrors } from "../components/TestingInfo";
import { cleanupDriverLicenceNumber, ensureUppercase, trimTextFields } from "../utils/forms";
import { applicationRefinement } from "../logic/dbsRules";
import { useRedirectToGDPRConsentIfRequired } from "./ApplicationGDPRConsent";
import FormButtons from "../components/FormButtons";
import { computeDriverLicenceNumberPrefix, documentDateOfBirthMismatch } from "../generated/dbs-rules";
import TestingData from "../components/TestingData";
import { identityDocuments } from "./ApplicationMeetingData";
import { useActionPath } from "./ApplicationUpdate";
import TestApplicationAlert from "../components/TestApplicationAlert";
import useCheckDriverLicenceNumberAgainstNames from "../hooks/useCheckDriverLicenceNumberAgainstNames";

export const AlternativeValidDocuments: React.FC = () =>
  <ul>
    {Object.entries(identityDocuments.group1 ?? {}).map(([key, value]) => {
      if (key === 'passport' || key === 'driverLicence') return null
      return <li key={key}>{value.title}</li>
    })}
  </ul>

export const Element: React.FC = () => {
  const { application_id } = useParams<{ application_id?: string }>()
  if (!application_id) throw new Error('application_id is required')

  const getEBulkApplicationByID = apiClientHooks.useGetEBulkApplicationByID({ params: { id: application_id } })
  const readonly = getEBulkApplicationByID.data?.status !== 'draft'
  console.log("ApplicationUpdateDocuments.Element: ", { getEBulkApplicationByID })

  useRedirectToGDPRConsentIfRequired(application_id)
  const actionPath = useActionPath('homepage')

  // Build the Form
  const form = useForm<LocalFormSchema>({
    resolver: zodResolver(
      zLocalFormSchema
        .superRefine(applicationRefinement('documents'))
    ),
    mode: 'onBlur',
    defaultValues: {
      ...getEBulkApplicationByID.data?.data as any, // NOTE: This is a hack, as I don't understand the typescript error!
      _hasPassport: getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.PassportDetails?.PassportNumber != null,
      _hasDriverLicence: getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.DriverLicenceDetails?.DriverLicenceNumber != null,
      ApplicantIdentityDetails: {
        ...getEBulkApplicationByID.data?.data.ApplicantIdentityDetails ?? {},
        PassportDetails: {
          ...getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.PassportDetails ?? {},
          PassportNumber: getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.PassportDetails?.PassportNumber ?? null,
          PassportIssueDate: getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.PassportDetails?.PassportIssueDate ?? null,
          PassportDob: getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.PassportDetails?.PassportDob ?? null,
          PassportNationality: getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.PassportDetails?.PassportNationality ?? null,
        },
        DriverLicenceDetails: {
          ...getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.DriverLicenceDetails ?? {},
          DriverLicenceNumber: getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.DriverLicenceDetails?.DriverLicenceNumber ?? null,
          DriverLicenceType: getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.DriverLicenceDetails?.DriverLicenceType ?? null,
          DriverLicenceDOB: getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.DriverLicenceDetails?.DriverLicenceDOB ?? null,
          DriverLicenceValidFrom: getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.DriverLicenceDetails?.DriverLicenceValidFrom ?? null,
          DriverLicenceIssueCountry: getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.DriverLicenceDetails?.DriverLicenceIssueCountry ?? null,
        },
      },
      NonApplicationFields: {
        ...getEBulkApplicationByID.data?.data.NonApplicationFields ?? {},
        NoPassportOrDriverLicence: getEBulkApplicationByID.data?.data.NonApplicationFields?.NoPassportOrDriverLicence ?? false,
      },
    },
  })

  const _hasPassport = form.watch('_hasPassport')
  const _hasDriverLicence = form.watch('_hasDriverLicence')
  const _clearFields = form.watch('_clearFields')

  const dob = form.watch('DateOfBirth')
  const passportDob = form.watch('ApplicantIdentityDetails.PassportDetails.PassportDob')
  const driverLicenceDob = form.watch('ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceDOB')
  // A document date of birth that disagrees with the Birth section is flagged here but does not stop the save: we cannot
  // tell which of the two is wrong, and the applicant must not lose this page. Submission is blocked until they agree (DBS-264)
  const passportDobMismatch = documentDateOfBirthMismatch('passport', passportDob, dob)
  const driverLicenceDobMismatch = documentDateOfBirthMismatch('driver licence', driverLicenceDob, dob)

  const driverLicenceNameErrors = useCheckDriverLicenceNumberAgainstNames(form)

  // Build the output
  const textFieldElementCommonProps = {
    control: form.control,
    margin: "normal" as const,
    fullWidth: true,
  }

  return <>
    <Helmet>
      <title>{readonly ? 'View' : 'Update'} Documents</title>
    </Helmet>
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h5" marginBottom={3}>
          {readonly ? 'View' : 'Update'} DBS Application
        </Typography>
        <TestApplicationAlert dbsApp={getEBulkApplicationByID.data} sx={{ marginBottom: 3 }} />

        <Form action={actionPath.url} method="post" onBlur={() => {
          trimTextFields(form)
          ensureUppercase(form, 'ApplicantIdentityDetails.PassportDetails.PassportNumber')
          ensureUppercase(form, 'ApplicantIdentityDetails.PassportDetails.PassportNationality')
          ensureUppercase(form, 'ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceNumber')
          cleanupDriverLicenceNumber(form)
        }}>
          {!readonly && (
            <TestingInfoBlock>
              <TestingData form={form} page="documents" />
            </TestingInfoBlock>
          )}
          <Typography component="h3" variant="h6" marginTop={3} marginBottom={1}>
            Passport
          </Typography>
          <CheckboxElement
            control={form.control}
            label="I have a valid passport matching my current full name"
            name="_hasPassport"
            onChange={event => {
              if (event.target.checked) {
                // We have a passport, so clear the checkbox for no passport / driver licence
                form.setValue('NonApplicationFields.NoPassportOrDriverLicence', false)
                // Add values which error if submitted without being changed
                form.setValue('ApplicantIdentityDetails.PassportDetails.PassportNumber', form.getValues('ApplicantIdentityDetails.PassportDetails.PassportNumber') ?? '')
                form.setValue('ApplicantIdentityDetails.PassportDetails.PassportIssueDate', form.getValues('ApplicantIdentityDetails.PassportDetails.PassportIssueDate') ?? '')
                form.setValue('ApplicantIdentityDetails.PassportDetails.PassportDob', form.getValues('ApplicantIdentityDetails.PassportDetails.PassportDob') ?? '')
                form.setValue('ApplicantIdentityDetails.PassportDetails.PassportNationality', form.getValues('ApplicantIdentityDetails.PassportDetails.PassportNationality') ?? '')
                // Don't clear the field!
                form.setValue('_clearFields', { ..._clearFields, 'ApplicantIdentityDetails/PassportDetails': 'false' })
              } else {
                // Remove the passportDetails if the checkbox is unchecked
                form.setValue('ApplicantIdentityDetails.PassportDetails.PassportNumber', null)
                form.setValue('ApplicantIdentityDetails.PassportDetails.PassportIssueDate', null)
                form.setValue('ApplicantIdentityDetails.PassportDetails.PassportDob', null)
                form.setValue('ApplicantIdentityDetails.PassportDetails.PassportNationality', null)
                // Remove the errors
                form.trigger('ApplicantIdentityDetails.PassportDetails.PassportNumber')
                form.trigger('ApplicantIdentityDetails.PassportDetails.PassportIssueDate')
                form.trigger('ApplicantIdentityDetails.PassportDetails.PassportDob')
                form.trigger('ApplicantIdentityDetails.PassportDetails.PassportNationality')
                // Clear the field
                form.setValue('_clearFields', { ..._clearFields, 'ApplicantIdentityDetails/PassportDetails': 'true' })
              }
            }}
            disabled={readonly}
          />
          {_hasPassport && <>
            <TestingInfoBlock>
              <Alert severity="info" sx={{ marginTop: 3 }}>
                <AlertTitle>Only shown in Test Mode</AlertTitle>
                An example of a UK passport number is 123456789.
              </Alert>
            </TestingInfoBlock>
            <Alert severity="info" sx={{ marginTop: 3 }}>
              <AlertTitle>Required Identity Document</AlertTitle>
              As you have a passport, you <strong>must</strong> use it as one of your primary forms of ID and bring it with you to your document verification meeting.
            </Alert>
            <TextFieldElement {...textFieldElementCommonProps} label="Passport Number" name="ApplicantIdentityDetails.PassportDetails.PassportNumber" disabled={readonly} />
            <StringFormattedControlledDatePicker
              control={form.control}
              sx={{ marginTop: 2, marginBottom: 1 }}
              label="Issue Date"
              name="ApplicantIdentityDetails.PassportDetails.PassportIssueDate"
              views={['day', 'month', 'year']}
              format="yyyy-MM-dd"
              disabled={readonly}
            />
            <TestingInfoBlock>
              <Alert severity="info" sx={{ marginTop: 3 }}>
                <AlertTitle>Only shown in Test Mode</AlertTitle>
                Your Date of Birth entered in the Birth section is <strong>{dob}</strong>.
              </Alert>
            </TestingInfoBlock>
            <StringFormattedControlledDatePicker
              control={form.control}
              sx={{ marginTop: 2, marginBottom: 1 }}
              label="Date of Birth (Passport)"
              name="ApplicantIdentityDetails.PassportDetails.PassportDob"
              views={['day', 'month', 'year']}
              format="yyyy-MM-dd"
              warningPopover={
                dob && dob !== passportDob
                  ? <>This doesn't match the Date of Birth provided elsewhere.</>
                  : driverLicenceDob && driverLicenceDob !== passportDob
                    ? <>This doesn't match the Date of Birth provided with your driver licence details.</>
                    : undefined
              }
              disabled={readonly}
            />
            {passportDobMismatch && <Alert severity="warning" sx={{ marginTop: 1 }} data-testid="dob-mismatch-warning">
              <AlertTitle>Please check the dates of birth</AlertTitle>
              {passportDobMismatch}
              <Typography variant="body2" sx={{ marginTop: 1 }}>
                <strong>You can still save this page.</strong> You will need to correct one of the dates before you can submit your application.
              </Typography>
            </Alert>}
            <TextFieldElement {...textFieldElementCommonProps} label="Nationality" name="ApplicantIdentityDetails.PassportDetails.PassportNationality" disabled={readonly} />
          </>}

          <Typography component="h3" variant="h6" marginTop={3} marginBottom={1}>
            Driver Licence
          </Typography>
          <CheckboxElement
            control={form.control}
            label="I have a valid driver licence matching my current full name and address"
            name="_hasDriverLicence"
            onChange={event => {
              if (event.target.checked) {
                // We have a driver licence, so clear the checkbox for no passport / driver licence
                form.setValue('NonApplicationFields.NoPassportOrDriverLicence', false)
                // Add values which error if submitted without being changed
                form.setValue('ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceNumber', form.getValues('ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceNumber') ?? '')
                form.setValue('ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceDOB', form.getValues('ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceDOB') ?? '')
                form.setValue('ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceType', form.getValues('ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceType') ?? '' as any)
                form.setValue('ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceValidFrom', form.getValues('ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceValidFrom') ?? '')
                form.setValue('ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceIssueCountry', form.getValues('ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceIssueCountry') ?? '' as any)
                // Don't clear the field!
                form.setValue('_clearFields', { ..._clearFields, 'ApplicantIdentityDetails/DriverLicenceDetails': 'false' })
              } else {
                // Remove the driverLicenceDetails if the checkbox is unchecked
                form.setValue('ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceNumber', null)
                form.setValue('ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceDOB', null)
                form.setValue('ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceType', null)
                form.setValue('ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceValidFrom', null)
                form.setValue('ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceIssueCountry', null)
                // Remove the errors
                form.trigger('ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceNumber')
                form.trigger('ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceDOB')
                form.trigger('ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceType')
                form.trigger('ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceValidFrom')
                form.trigger('ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceIssueCountry')
                // Clear the field
                form.setValue('_clearFields', { ..._clearFields, 'ApplicantIdentityDetails/DriverLicenceDetails': 'true' })
              }
            }}
            disabled={readonly}
          />
          {_hasDriverLicence && <>
            <TestingInfoBlock>
              <Alert severity="info" sx={{ marginTop: 3 }}>
                <AlertTitle>Only shown in Test Mode</AlertTitle>
                Your driver licence number must start with <strong>{computeDriverLicenceNumberPrefix(getEBulkApplicationByID.data?.data ?? {} as any)}</strong>.
              </Alert>
            </TestingInfoBlock>
            <Alert severity="info" sx={{ marginTop: 3 }}>
              <AlertTitle>Required Identity Document</AlertTitle>
              As you have a driver licence, you <strong>must</strong> use it as one of your primary forms of ID and bring it with you to your document verification meeting.
            </Alert>
            <Box sx={{ marginTop: 2, marginBottom: 1, position: 'relative', width: '100%', paddingBottom: '80%' }}>
              <img src="/uk-driver-licence.jpg" alt="Driver Licence Instructions" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain' }} />
            </Box>
            <TextFieldElement {...textFieldElementCommonProps} label="Driver Licence Number" name="ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceNumber" disabled={readonly} />
            {driverLicenceNameErrors.length > 0 && <Alert severity="warning" sx={{ marginTop: 1 }}>
              <AlertTitle>Driver Licence Name Errors</AlertTitle>
              <Stack spacing={1}>
                {driverLicenceNameErrors.map((error, index) => (
                  <div key={index}>{error}</div>
                ))}
              </Stack>
              <Typography variant="body2" sx={{ marginTop: 1 }}>
                <strong>Please check this reflects your true identity.</strong>
              </Typography>
            </Alert>}
            <TestingInfoBlock>
              <Alert severity="info" sx={{ marginTop: 3 }}>
                <AlertTitle>Only shown in Test Mode</AlertTitle>
                Your Date of Birth entered in the Birth section is <strong>{dob}</strong>.
              </Alert>
            </TestingInfoBlock>
            <StringFormattedControlledDatePicker
              control={form.control}
              sx={{ marginTop: 2, marginBottom: 1 }}
              label="Date of Birth (Licence)"
              name="ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceDOB"
              views={['day', 'month', 'year']}
              format="yyyy-MM-dd"
              warningPopover={
                dob && dob !== driverLicenceDob
                  ? <>This doesn't match the Date of Birth provided elsewhere.</>
                  : passportDob && passportDob !== driverLicenceDob
                    ? <>This doesn't match the Date of Birth provided with your passport details.</>
                    : undefined
              }
              disabled={readonly}
            />
            {driverLicenceDobMismatch && <Alert severity="warning" sx={{ marginTop: 1 }} data-testid="dob-mismatch-warning">
              <AlertTitle>Please check the dates of birth</AlertTitle>
              {driverLicenceDobMismatch}
              <Typography variant="body2" sx={{ marginTop: 1 }}>
                <strong>You can still save this page.</strong> You will need to correct one of the dates before you can submit your application.
              </Typography>
            </Alert>}
            <EnumBackedAutocomplete
              control={form.control}
              sx={{ marginTop: 2, marginBottom: 1 }}
              label="Licence Type"
              name="ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceType"
              options={asOptions(driverLicenceTypeMapping)}
              disabled={readonly}
            />
            <StringFormattedControlledDatePicker
              control={form.control}
              sx={{ marginTop: 2, marginBottom: 1 }}
              label="Valid From"
              name="ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceValidFrom"
              views={['day', 'month', 'year']}
              format="yyyy-MM-dd"
              disabled={readonly}
            />
            <EnumBackedAutocomplete
              control={form.control}
              sx={{ marginTop: 2 }}
              label="Issuing Country"
              name="ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceIssueCountry"
              options={asOptions(countryCodeMapping)}
              disabled={readonly}
            />
          </>}

          {!_hasPassport && !_hasDriverLicence && <>
            <Alert severity="warning" sx={{ marginTop: 3 }}>
              <AlertTitle>No Passport or Driver Licence?</AlertTitle>
              It is possible to proceed without a passport or driver licence, but you will need a valid form of at least one of the following documents:
              <AlternativeValidDocuments />

              <CheckboxElement
                control={form.control}
                label="I do not have a passport or driver licence but I have one of the other documents listed above"
                name="NonApplicationFields.NoPassportOrDriverLicence"
                inputProps={{ name: 'NonApplicationFields.NoPassportOrDriverLicence' }}
                disabled={readonly}
              />
            </Alert>
          </>}

          <HiddenInputForClearFields _clearFields={_clearFields} />

          <TestingInfoBlockFormErrors form={form} />

          <FormButtons actionPath={actionPath} form={form} readonly={readonly} />
        </Form>
      </Box>
    </Container>
  </>
}

export default Element
